import {
  AdminUserDetail,
  LeadCatalogRef,
  LeadProfileCatalogEntry,
} from 'models/Lead';

export interface CatalogOption {
  value: number;
  label: string;
}

/** Bilingual label for the UI language; falls back to the localized label. */
export const getCatalogEntryLabel = (
  entry: LeadProfileCatalogEntry,
  language: string
): string => {
  const bilingual = language.startsWith('en') ? entry.labelEn : entry.labelEs;
  return bilingual || entry.label;
};

/**
 * Select options in catalog order. `current` is the value stored on the lead:
 * when the catalog no longer lists it (inactive row) it is appended so the
 * select keeps showing its name instead of a bare id.
 */
export const toCatalogOptions = (
  entries: LeadProfileCatalogEntry[],
  language: string,
  current?: LeadCatalogRef | null
): CatalogOption[] => {
  const options = entries.map((entry) => ({
    value: entry.id,
    label: getCatalogEntryLabel(entry, language),
  }));
  if (current && !options.some((option) => option.value === current.id)) {
    options.push({ value: current.id, label: current.name });
  }
  return options;
};

export interface CatalogIds {
  nationalityId: number | null;
  genderId: number | null;
  maritalStatusId: number | null;
}

/** Catalog ids stored on the lead; `null` when the field is empty. */
export const getCatalogIds = (detail: AdminUserDetail): CatalogIds => ({
  nationalityId:
    detail.nationality?.id ?? detail.profile?.nationalityId ?? null,
  genderId: detail.gender?.id ?? null,
  maritalStatusId: detail.maritalStatus?.id ?? null,
});

/**
 * PATCH keys for the catalog fields the user actually changed. Omitted keys
 * stay unchanged upstream, and a cleared select (`undefined`) sends `null`.
 */
export const buildCatalogIdPatch = (
  current: CatalogIds,
  next: { [K in keyof CatalogIds]?: number | null }
): Partial<CatalogIds> => {
  const patch: Partial<CatalogIds> = {};
  (Object.keys(current) as (keyof CatalogIds)[]).forEach((key) => {
    const value = next[key] ?? null;
    if (value !== current[key]) patch[key] = value;
  });
  return patch;
};
